import React, { useEffect, useState, lazy, Suspense } from 'react';
// import { useLocation } from 'react-router-dom';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { zonedTimeToUtc } from 'date-fns-tz';
import { differenceInSeconds, parseISO, format } from 'date-fns';
import { useGetUserData } from '@hooks/useGetUserData';
import trackUse from '@utils/trackUse';
import config from '@configFile';
import {
  daysUntil,
  isPrimeDay,
  getCurrentDealEventStrObj,
  addAffiliateTagToLink,
  logPostHogClick
} from '@utils/index';
import PrimeRibbon from '../PrimeRibbon';
import Ticker from '../Ticker';
import { LoadingSkeleton, getMarginStyle, getEventDayEvent } from './utils';

const Snowfall = lazy(() => import('react-snowfall'));

const CountdownBanner = () => {
  const { data: user } = useGetUserData();
  const currentEventObj = getCurrentDealEventStrObj();

  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const futureLeadEvent = getEventDayEvent(currentEventObj) || null;
  const futureEventTitle = futureLeadEvent?.title || 'Event';

  const eventTime = parseISO(
    zonedTimeToUtc(
      `${futureLeadEvent?.start || format(new Date(), 'yyyy-MM-dd')}T00:00:01`,
      'America/Los_Angeles'
    ).toISOString()
  );

  const logClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const clickType = 'countdown-banner';
    const linkClicked = e.currentTarget.href;
    const tag =
      currentEventObj?.affiliateTag || config.AFFILIATE_TAGS.GIFT_GUIDE;
    const item = futureEventTitle;
    const href = linkClicked;
    const toLog = {
      ...user
    };

    delete toLog?.dealsIdeasPending;
    delete toLog?.allUsedCoupons;

    trackUse({
      item: item || clickType || tag,
      value: href,
      type: 'CLICK'
    });

    logPostHogClick(href, clickType, tag, item, toLog);
  };

  const showLiveMessage = () => {
    return (
      <Typography
        component="a"
        href={
          currentEventObj?.navLinkHref
            ? currentEventObj?.navLinkHref
            : addAffiliateTagToLink(
                currentEventObj?.saleLink,
                currentEventObj?.affiliateTag ||
                  config.AFFILIATE_TAGS.GIFT_GUIDE
              )
        }
        onClick={logClick}
        target={currentEventObj?.navLinkHref ? '_self' : '_blank'}
        variant="h5"
        color="primary.contrastText"
        sx={{
          textDecoration: 'none',
          fontWeight: 500,
          '@media (max-width: 500px)': {
            fontWeight: 700
          }
        }}
      >
        {currentEventObj?.overrideCountdownMessage ||
          `${currentEventObj?.label || 'Event'} deals are live!`}
      </Typography>
    );
  };

  const renderEmptyBlock = () => {
    const currentEventObj = getCurrentDealEventStrObj();
    if (currentEventObj) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          padding={1}
          bgcolor={currentEventObj?.overrideBannerBgColor || '#273377'}
          color="primary.contrastText"
          data-testid="countdown-banner-block"
          position="relative"
          sx={{
            // no underline
            minHeight: {
              xs: 72.5,
              sm: 56
            },
            lineHeight: 1.15,
            WebkitTextSizeAdjust: '100%',
            backgroundRepeat: 'repeat-x',
            backgroundPosition: `top -4px center`,
            backgroundImage: !currentEventObj?.showBannerConfetti
              ? 'initial'
              : `url("data:image/svg+xml,%3Csvg width='574' height='60' viewBox='0 0 574 60' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect opacity='0.8' x='27.1224' y='20.0458' width='5' height='13' transform='rotate(-139 27.1224 20.0458)' fill='%23F23B14'/%3E%3Crect opacity='0.8' x='118.478' y='7.00201' width='5' height='13' transform='rotate(-38.8114 118.478 7.00201)' fill='%23FBBA23'/%3E%3Crect opacity='0.8' x='504.616' y='25.4479' width='5' height='13' transform='rotate(-60.2734 504.616 25.4479)' fill='%23F23B14'/%3E%3Crect opacity='0.6' x='538.983' y='45.555' width='5' height='13' transform='rotate(16.7826 538.983 45.555)' fill='%232A2F6A'/%3E%3Crect opacity='0.3' x='470.322' y='2.63625' width='5' height='13' transform='rotate(11.295 470.322 2.63625)' fill='%2333AAFF'/%3E%3Crect opacity='0.3' x='190.295' y='4.58138' width='5' height='13' transform='rotate(27.5954 190.295 4.58138)' fill='%23F23B14'/%3E%3Crect opacity='0.8' x='234.303' y='16.3233' width='5' height='13' transform='rotate(-41.8233 234.303 16.3233)' fill='%2365BB5C'/%3E%3Crect opacity='0.6' x='369.702' y='40.9875' width='5' height='13' transform='rotate(-56.419 369.702 40.9875)' fill='%2333AAFF'/%3E%3Crect opacity='0.3' x='402.121' y='31.0848' width='5' height='13' transform='rotate(-17.9234 402.121 31.0848)' fill='%23F23B14'/%3E%3Crect opacity='0.6' x='200.316' y='31.9328' width='5' height='13' transform='rotate(-15.8896 200.316 31.9328)' fill='%232A2F6A'/%3E%3Crect opacity='0.6' x='69.6745' y='23.4725' width='6' height='10' transform='rotate(70.0266 69.6745 23.4725)' fill='%2365BB5C'/%3E%3Crect opacity='0.6' x='291.945' y='7.16931' width='6' height='10' transform='rotate(30.4258 291.945 7.16931)' fill='%23FBBA23'/%3E%3Crect opacity='0.3' x='33.7754' y='38.2208' width='6' height='10' transform='rotate(38.6056 33.7754 38.2208)' fill='%23FBBA23'/%3E%3Crect opacity='0.8' x='109.752' y='31.1743' width='6' height='10' transform='rotate(28.5296 109.752 31.1743)' fill='%2333AAFF'/%3E%3Crect opacity='0.3' x='278.081' y='37.8695' width='6' height='10' transform='rotate(-26.5651 278.081 37.8695)' fill='%23F23B14'/%3E%3Crect opacity='0.8' x='416.294' y='11.5573' width='6' height='10' transform='rotate(-22.8498 416.294 11.5573)' fill='%23FBBA23'/%3E%3Crect opacity='0.3' x='354.667' y='9.32341' width='6' height='10' transform='rotate(17.7506 354.667 9.32341)' fill='%232A2F6A'/%3E%3Crect opacity='0.8' x='532.404' y='16.6372' width='6' height='10' transform='rotate(-75.3432 532.404 16.6372)' fill='%23FBBA23'/%3E%3Crect opacity='0.6' x='460.463' y='39.3557' width='6' height='10' transform='rotate(45.4982 460.463 39.3557)' fill='%2365BB5C'/%3E%3C/svg%3E")`
          }}
        >
          {isSnowfallEnabled && currentEventObj?.showSnowFall && (
            <Suspense fallback={<div>*</div>}>
              <Snowfall snowflakeCount={50} />
            </Suspense>
          )}
          {isPrimeDay() ? (
            <PrimeRibbon />
          ) : currentEventObj.isEarlySale ? (
            <LoadingSkeleton />
          ) : (
            showLiveMessage()
          )}
        </Box>
      );
    }
    return null;
  };

  const totalSecondsLeft = differenceInSeconds(eventTime, new Date());
  const [isSnowfallEnabled, setIsSnowfallEnabled] = useState(false);

  useEffect(() => {
    // Example: Enable snowfall if the event matches certain criteria
    setIsSnowfallEnabled(true);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const totalSecondsLeft = differenceInSeconds(eventTime, new Date());
      const days = Math.floor(totalSecondsLeft / (3600 * 24));
      const hours = Math.floor((totalSecondsLeft / 3600) % 24);
      const minutes = Math.floor((totalSecondsLeft / 60) % 60);
      const seconds = totalSecondsLeft % 60;

      setTimeLeft({ days, hours, minutes, seconds });

      if (totalSecondsLeft > 0 && totalSecondsLeft <= 2) {
        if (typeof window !== 'undefined') {
          // refresh when timer is about to hit zero
          window.location.reload();
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [futureLeadEvent, eventTime]);

  const getDaysUntilNextEvent = () => {
    return daysUntil(futureLeadEvent?.start || '') || 0;
  };

  const marginStyle = getMarginStyle(isMobile);

  // to do banner if prime day is live
  if (totalSecondsLeft <= 0) {
    return renderEmptyBlock();
  }

  // prevent flash of banner with 0s
  if (
    timeLeft.seconds === 0 &&
    timeLeft.minutes === 0 &&
    timeLeft.hours === 0 &&
    timeLeft.days === 0
  ) {
    return renderEmptyBlock();
  }

  if (
    futureLeadEvent &&
    getDaysUntilNextEvent() >= config.DAYS_BEFORE_EVENT_COUNTER
  ) {
    return renderEmptyBlock();
  }

  const mobileStyle = 'body2';
  const desktopStyle = 'h6';

  if (!futureLeadEvent) {
    return null;
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      padding={1}
      bgcolor={currentEventObj?.overrideBannerBgColor || '#273377'}
      color="primary.contrastText"
      data-testid="countdown-banner"
      position="relative"
    >
      {isSnowfallEnabled && currentEventObj?.showSnowFall && (
        <Suspense fallback={<div>*</div>}>
          <Snowfall snowflakeCount={50} />
        </Suspense>
      )}
      {futureLeadEvent &&
      getDaysUntilNextEvent() < config.DAYS_BEFORE_EVENT_COUNTER ? (
        <Box
          width="100%"
          display="flex"
          justifyContent="center"
          sx={{
            flexDirection: isMobile ? 'column' : 'row'
          }}
        >
          <Typography
            component="a"
            href={addAffiliateTagToLink(
              currentEventObj.saleLink,
              currentEventObj?.affiliateTag || config.AFFILIATE_TAGS.GIFT_GUIDE
            )}
            target="_blank"
            variant={isMobile ? mobileStyle : desktopStyle}
            color="primary.contrastText"
            sx={{
              ...marginStyle,
              fontWeight: 500,
              // no underline
              textDecoration: 'none',
              '@media (max-width: 500px)': {
                fontWeight: 700
              }
            }}
            onClick={logClick}
          >
            {currentEventObj?.overrideCountdownMessage ||
              `Countdown to ${futureEventTitle}:`}
          </Typography>
          <Ticker endDate={eventTime} />
        </Box>
      ) : null}
    </Box>
  );
};

export default CountdownBanner;
